import React from "react";

import AddIcon from "@mui/icons-material/Add";
import { Button, Chip, Paper, Stack, TextField, Typography } from "@mui/material";

interface ClientWhitelistingTabProps {
  initialDomainWhiteList: string[];
  onSave: (domainWhiteList: string[]) => void;
  isSaving?: boolean;
}

const normalizeDomain = (value: string) => value.trim().toLowerCase();

const isValidDomainEntry = (value: string): boolean => {
  if (!value) {
    return false;
  }

  if (value.includes(" ") || value.includes("://") || value.includes("/") || value.includes(":")) {
    return false;
  }

  const domain = value.toLowerCase();

  if (
    domain.length > 100 ||
    domain.startsWith(".") ||
    domain.endsWith(".") ||
    domain.includes("..")
  ) {
    return false;
  }

  const labels = domain.split(".");
  if (labels.length < 2) return false;

  const labelRegex = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/i;
  if (!labels.every((label) => labelRegex.test(label))) return false;

  const tld = labels[labels.length - 1];
  return /[a-z]/i.test(tld) || /^xn--/i.test(tld);
};

const ClientWhitelistingTab: React.FC<ClientWhitelistingTabProps> = ({
  initialDomainWhiteList,
  onSave,
  isSaving,
}) => {
  const [domainInput, setDomainInput] = React.useState("");
  const [domainError, setDomainError] = React.useState("");
  const [domainWhiteList, setDomainWhiteList] = React.useState<string[]>(
    initialDomainWhiteList.map(normalizeDomain)
  );

  React.useEffect(() => {
    setDomainWhiteList(initialDomainWhiteList.map(normalizeDomain));
    setDomainInput("");
    setDomainError("");
  }, [initialDomainWhiteList]);

  const addDomain = () => {
    const domain = normalizeDomain(domainInput);

    if (!domain) {
      return;
    }

    if (!isValidDomainEntry(domain)) {
      setDomainError("Enter a valid domain");
      return;
    }

    if (domainWhiteList.includes(domain)) {
      setDomainError("Domain already added");
      return;
    }

    const nextDomainWhiteList = [...domainWhiteList, domain];
    setDomainWhiteList(nextDomainWhiteList);
    setDomainInput("");
    setDomainError("");
    onSave(nextDomainWhiteList);
  };

  const removeDomain = (domain: string) => {
    const nextDomainWhiteList = domainWhiteList.filter((item) => item !== domain);
    setDomainWhiteList(nextDomainWhiteList);
    onSave(nextDomainWhiteList);
  };


  return (
    <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Email domain whitelisting allows you to restrict which email domains can be used for <b>substitute meeting attendees</b>.
        That is, if an attendee can no longer attend a meeting they are able to invite a substitute attendee to take their place.
        For security reasons, you may want to restrict which email domains can be used for substitute attendees.
        The whitelist is not applied to the general addition of attendees to meetings by meeting hosts, nor to the addition of users to your client.
      </Typography>

      <Stack spacing={1}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            minHeight: 64,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            borderStyle: "dashed",
          }}
        >
          {domainWhiteList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No domains added yet
            </Typography>
          ) : (
            domainWhiteList.map((domain) => (
              <Chip
                key={domain}
                label={domain}
                onDelete={() => removeDomain(domain)}
                color="primary"
                variant="outlined"
              />
            ))
          )}
        </Paper>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            size="small"
            label="Add domain"
            placeholder="example.com"
            value={domainInput}
            error={Boolean(domainError)}
            helperText={domainError}
            onChange={(event) => {
              setDomainInput(event.target.value);
              if (domainError) {
                setDomainError("");
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addDomain();
              }
            }}
          />
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addDomain}
            disabled={isSaving}
          >
            Add
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ClientWhitelistingTab;
